import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
const SECTIONS = [{
  id: 'prerequisites',
  label: 'Before You Start'
}, {
  id: 'installation',
  label: 'Installation'
}, {
  id: 'credentials',
  label: 'Australia Post Credentials'
}, {
  id: 'sender-address',
  label: 'Sender Address'
}, {
  id: 'parcel-sizes',
  label: 'Parcel Sizes'
}, {
  id: 'creating-shipments',
  label: 'Creating Shipments'
}, {
  id: 'labels',
  label: 'Printing Labels'
}, {
  id: 'manifests',
  label: 'Manifests'
}, {
  id: 'address-validation',
  label: 'Address Validation'
}, {
  id: 'postcode-rules',
  label: 'Postcode Rules',
  comingSoon: true
}, {
  id: 'troubleshooting',
  label: 'Troubleshooting'
}];
const SCREENSHOTS = {
  credentials: 'https://assets.corkship.io/Corkship%20settings%20comm7.png',
  dashboard: 'https://assets.corkship.io/Comm7%20listing%20image%20Dashboard.png',
  parcelSizes: 'https://assets.corkship.io/Parcel%20sizes%20settings%20comm7.png',
  shipments: 'https://assets.corkship.io/comm7%20listing%20Shipments.png',
  manifests: 'https://assets.corkship.io/comm7%20listing%20manifest.png',
  labels: 'https://assets.corkship.io/generate%20labels%20comm7%20listing.png'
};
function Screenshot({
  src,
  alt,
  caption
}: {
  src: string;
  alt: string;
  caption?: string;
}) {
  return <figure className="my-6" data-id="element-83">
      <div className="rounded-xl overflow-hidden border border-[#E2E8F0] shadow-md bg-[#F8FAFC]" data-id="element-84">
        <img src={src} alt={alt} className="w-full h-auto block" loading="lazy" data-id="element-85" />
      </div>
      {caption && <figcaption className="text-center text-xs text-[#94A3B8] mt-2" data-id="element-86">{caption}</figcaption>}
    </figure>;
}
function StepBadge({
  n
}: {
  n: number;
}) {
  return <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#0284C7] text-white text-sm font-bold flex-shrink-0" data-id="element-87">{n}</span>;
}
function Callout({
  type,
  children
}: {
  type: 'info' | 'warning' | 'tip';
  children: React.ReactNode;
}) {
  const styles = {
    info: {
      bg: 'bg-[#EFF6FF]',
      border: 'border-[#0284C7]/30',
      icon: '💡',
      text: 'text-[#0369A1]'
    },
    warning: {
      bg: 'bg-[#FFFBEB]',
      border: 'border-[#F59E0B]/30',
      icon: '⚠️',
      text: 'text-[#92400E]'
    },
    tip: {
      bg: 'bg-[#F0FDF4]',
      border: 'border-[#10B981]/30',
      icon: '✓',
      text: 'text-[#065F46]'
    }
  }[type];
  return <div className={`${styles.bg} border ${styles.border} rounded-xl p-4 flex gap-3 my-5`} data-id="element-88">
      <span className="text-lg leading-tight flex-shrink-0" data-id="element-89">{styles.icon}</span>
      <p className={`text-sm leading-relaxed ${styles.text} m-0`} data-id="element-90">{children}</p>
    </div>;
}
function SectionHeading({
  id,
  children
}: {
  id: string;
  children: React.ReactNode;
}) {
  return <h2 id={id} className="font-heading text-2xl font-bold text-[#0F172A] mt-12 mb-6 pb-3 border-b border-[#E2E8F0] scroll-mt-24" data-id="element-91">{children}</h2>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="font-heading text-lg font-bold text-[#0F172A] mt-8 mb-3" data-id="element-92">{children}</h3>;
}
function Step({
  n,
  title,
  children
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return <div className="flex gap-4 mb-6" data-id="element-93">
      <StepBadge n={n} data-id="element-94" />
      <div className="flex-1" data-id="element-95">
        <p className="font-semibold text-[#0F172A] text-[15px] mb-1" data-id="element-96">{title}</p>
        <div className="text-[#475569] text-[15px] leading-relaxed" data-id="element-97">{children}</div>
      </div>
    </div>;
}
export function DocsCommerce7Page() {
  const [activeSection, setActiveSection] = useState('prerequisites');
  useEffect(() => {
    const handleScroll = () => {
      for (const section of [...SECTIONS].reverse()) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(section.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <div className="min-h-screen flex flex-col bg-[#F1F5F9]" data-id="element-98">

      {/* Top Nav */}
      <header className="fixed top-0 left-0 right-0 z-[1000] bg-[#F1F5F9]/90 backdrop-blur-xl border-b border-[#0F172A]/[0.08]" data-id="element-99">
        <div className="flex items-center justify-between py-4 max-w-[1200px] mx-auto px-6" data-id="element-100">
          <Link to="/" className="flex items-center gap-2 font-heading text-xl font-bold text-[#0F172A] no-underline" data-id="element-101">
            <img src="https://assets.corkship.io/CorkShip%20Logo%20Original%20png.png" alt="CorkShip" className="w-8 h-8 object-contain" data-id="element-102" />
            CorkShip
          </Link>
          <div className="hidden md:inline-flex items-center gap-2 text-sm text-[#64748B]" data-id="element-103">
            <span className="inline-flex items-center gap-1.5 bg-[#6B21A8]/10 text-[#6B21A8] px-3 py-1 rounded-full text-xs font-semibold" data-id="element-104">
              <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" data-id="element-105"><circle cx="12" cy="12" r="10" fill="#6B21A8" data-id="element-106" /><text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" data-id="element-107">C7</text></svg>
              Commerce7
            </span>
            Documentation
          </div>
        </div>
      </header>

      {/* Docs Hero */}
      <div className="pt-[80px] bg-[#0F172A] text-white" data-id="element-108">
        <div className="max-w-[1200px] mx-auto px-6 py-12" data-id="element-109">
          <div className="flex items-center gap-2 text-sm text-white/50 mb-4" data-id="element-110">
            <Link to="/" className="text-white/50 hover:text-white no-underline transition-colors" data-id="element-111">CorkShip</Link>
            <span data-id="element-112">/</span><span className="text-white/80" data-id="element-113">Documentation</span>
            <span data-id="element-114">/</span><span className="text-white" data-id="element-115">Commerce7</span>
          </div>
          <div className="flex items-start justify-between gap-8 flex-wrap" data-id="element-116">
            <div data-id="element-117">
              <div className="inline-flex items-center gap-2 bg-[#6B21A8]/30 text-[#C4B5FD] px-3 py-1.5 rounded-full text-xs font-semibold mb-4" data-id="element-118">
                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" data-id="element-119"><circle cx="12" cy="12" r="10" fill="#6B21A8" data-id="element-120" /><text x="12" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" data-id="element-121">C7</text></svg>
                Commerce7 Integration
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-122">Getting Started with CorkShip</h1>
              <p className="text-white/70 text-lg max-w-[560px] leading-relaxed" data-id="element-123">Connect CorkShip to your Commerce7 store and start creating Australia Post eParcel shipments directly from your admin.</p>
            </div>
            <div className="flex flex-col gap-3 text-sm" data-id="element-124">
              <div className="flex items-center gap-2 text-white/60" data-id="element-125">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 flex-shrink-0" data-id="element-126"><circle cx="12" cy="12" r="10" data-id="element-127" /><path d="M12 6v6l4 2" data-id="element-128" /></svg>
                ~5 min setup
              </div>
              <div className="flex items-center gap-2 text-white/60" data-id="element-129">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 flex-shrink-0" data-id="element-130"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" data-id="element-131" /></svg>
                Australia Post account required
              </div>
              <div className="flex items-center gap-2 text-white/60" data-id="element-132">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 flex-shrink-0" data-id="element-133"><rect x="2" y="7" width="20" height="14" rx="2" data-id="element-134" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" data-id="element-135" /></svg>
                Commerce7 admin access required
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex-1 max-w-[1200px] mx-auto px-6 py-10 w-full" data-id="element-136">
        <div className="flex gap-10 items-start" data-id="element-137">

          {/* Sidebar */}
          <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-24" data-id="element-138">
            <p className="text-xs font-semibold text-[#64748B] uppercase tracking-wider mb-3" data-id="element-139">On This Page</p>
            <nav className="flex flex-col gap-0.5" data-id="element-140">
              {SECTIONS.map(s => <a key={s.id} href={s.comingSoon ? undefined : `#${s.id}`} onClick={s.comingSoon ? e => e.preventDefault() : undefined} className={`text-sm px-3 py-2 rounded-lg no-underline transition-colors flex items-center justify-between gap-2 ${s.comingSoon ? 'text-[#CBD5E1] cursor-default' : activeSection === s.id ? 'bg-[#0284C7]/10 text-[#0284C7] font-semibold' : 'text-[#64748B] hover:text-[#0F172A] hover:bg-white'}`} data-id="element-141">
                  <span data-id="element-142">{s.label}</span>
                  {s.comingSoon && <span className="text-[10px] font-semibold bg-[#F1F5F9] text-[#94A3B8] px-1.5 py-0.5 rounded-full border border-[#E2E8F0] flex-shrink-0" data-id="element-143">Soon</span>}
                </a>)}
            </nav>
            <div className="mt-8 p-4 bg-white rounded-xl border border-[#E2E8F0]" data-id="element-144">
              <p className="text-xs font-semibold text-[#0F172A] mb-2" data-id="element-145">Need help?</p>
              <a href="mailto:support@corkship.io" className="text-xs text-[#0284C7] no-underline hover:underline" data-id="element-146">support@corkship.io</a>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0" data-id="element-147">
            <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-8 md:p-10" data-id="element-148">

              <SectionHeading id="prerequisites" data-id="element-149">Before You Start</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-150">Before installing CorkShip you'll need the following ready:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-151">
                {[{
                icon: '📦',
                title: 'Australia Post eParcel Account',
                desc: 'An active eParcel contract with Australia Post. CorkShip is a bring-your-own-account integration — your existing rates apply.'
              }, {
                icon: '🔑',
                title: 'eParcel API Credentials',
                desc: 'Your API Key, API Secret, and Account Number from the Australia Post MyPost Business portal.'
              }, {
                icon: '🛒',
                title: 'Commerce7 Admin Access',
                desc: 'Full admin access to your Commerce7 store to install the app and manage orders.'
              }, {
                icon: '📍',
                title: 'Sender Address',
                desc: "Your winery's dispatch address — this becomes the return address on all shipping labels."
              }].map(item => <div key={item.title} className="flex gap-3 p-4 bg-[#F8FAFC] rounded-xl border border-[#E2E8F0]" data-id="element-152">
                    <span className="text-2xl flex-shrink-0" data-id="element-153">{item.icon}</span>
                    <div data-id="element-154">
                      <p className="font-semibold text-[#0F172A] text-sm mb-1" data-id="element-155">{item.title}</p>
                      <p className="text-[#64748B] text-sm leading-relaxed" data-id="element-156">{item.desc}</p>
                    </div>
                  </div>)}
              </div>
              <Callout type="info" data-id="element-157">If you don't yet have an Australia Post eParcel account, visit <strong data-id="element-158">auspost.com.au/business/shipping/domestic</strong> to apply. Approval typically takes 2–5 business days.</Callout>

              <SectionHeading id="installation" data-id="element-159">Installation</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-6" data-id="element-160">CorkShip installs directly into your Commerce7 admin via the Commerce7 App Marketplace.</p>
              <Step n={1} title="Find CorkShip in the Commerce7 App Marketplace" data-id="element-161">Log in to your Commerce7 admin, navigate to <strong data-id="element-162">Apps</strong> in the sidebar, and search for <strong data-id="element-163">CorkShip</strong>.</Step>
              <Step n={2} title="Click Install" data-id="element-164">Click the Install button on the CorkShip listing. Commerce7 will ask you to confirm the permissions CorkShip requires — it only requests <strong data-id="element-165">Order: Full</strong> access.</Step>
              <Step n={3} title="Complete the setup wizard" data-id="element-166">After installation, CorkShip will appear in your Commerce7 sidebar. Click it to open the app and follow the setup steps below.</Step>
              <Screenshot src={SCREENSHOTS.dashboard} alt="CorkShip dashboard inside Commerce7 admin" caption="CorkShip opens as an embedded panel inside your Commerce7 admin" data-id="element-167" />
              <Callout type="tip" data-id="element-168">CorkShip opens inside your Commerce7 admin as an embedded panel — you never need to leave Commerce7 to create shipments or print labels.</Callout>

              <SectionHeading id="credentials" data-id="element-169">Australia Post Credentials</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-170">CorkShip connects directly to your Australia Post eParcel account. Your credentials are stored securely and never shared.</p>
              <SubHeading data-id="element-171">Where to find your credentials</SubHeading>
              <Step n={1} title="Log in to MyPost Business" data-id="element-172">Go to <strong data-id="element-173">auspost.com.au/mypost-business</strong> and sign in with your account.</Step>
              <Step n={2} title="Navigate to API Settings" data-id="element-174">Go to <strong data-id="element-175">Settings → Integrations → API</strong>. If you haven't generated API credentials before, click <strong data-id="element-176">Generate New Credentials</strong>.</Step>
              <Step n={3} title="Copy your three credentials" data-id="element-177">
                You'll need:
                <ul className="mt-2 space-y-1 list-none" data-id="element-178">
                  <li className="flex items-center gap-2" data-id="element-179"><span className="w-1.5 h-1.5 rounded-full bg-[#0284C7] flex-shrink-0" data-id="element-180"></span><span data-id="element-181"><strong data-id="element-182">API Key</strong> — a UUID-format string</span></li>
                  <li className="flex items-center gap-2" data-id="element-183"><span className="w-1.5 h-1.5 rounded-full bg-[#0284C7] flex-shrink-0" data-id="element-184"></span><span data-id="element-185"><strong data-id="element-186">API Secret</strong> — a short alphanumeric string</span></li>
                  <li className="flex items-center gap-2" data-id="element-187"><span className="w-1.5 h-1.5 rounded-full bg-[#0284C7] flex-shrink-0" data-id="element-188"></span><span data-id="element-189"><strong data-id="element-190">Account Number</strong> — your 10-digit eParcel account number</span></li>
                </ul>
              </Step>
              <Step n={4} title="Enter credentials in CorkShip" data-id="element-191">Open CorkShip in your Commerce7 admin, go to <strong data-id="element-192">Settings → Australia Post</strong>, paste in your three credentials, and click <strong data-id="element-193">Save &amp; Verify</strong>. CorkShip will test the connection live.</Step>
              <Screenshot src={SCREENSHOTS.credentials} alt="CorkShip Settings — Australia Post credentials" caption="Settings → Australia Post credentials" data-id="element-194" />
              <Callout type="warning" data-id="element-195">Keep your API credentials confidential. Do not share them via email or store them in plain text. CorkShip encrypts them at rest.</Callout>
              <SubHeading data-id="element-196">Sandbox vs Live Mode</SubHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-197">CorkShip supports both Australia Post sandbox (for testing) and live mode. You can toggle between them in <strong data-id="element-198">Settings → Australia Post → Mode</strong>. Shipments created in sandbox mode do not generate real labels or charges.</p>

              <SectionHeading id="sender-address" data-id="element-199">Sender Address</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-200">Your sender address is printed on every shipping label as the return address and is also used by Australia Post to determine eligible shipping routes and product codes.</p>
              <Step n={1} title="Go to Settings → Sender Address in CorkShip" data-id="element-201">Fill in your winery's full dispatch address — business name, street address, suburb, state, and postcode.</Step>
              <Step n={2} title="Ensure the postcode matches your eParcel contract" data-id="element-202">Australia Post routes shipments based on the sender postcode. If your contract postcode doesn't match your sender address, shipments may be rejected. Contact Australia Post if unsure.</Step>
              <Callout type="info" data-id="element-203">If you dispatch from multiple locations, contact us at support@corkship.io — multi-location sender support is on the roadmap.</Callout>

              <SectionHeading id="parcel-sizes" data-id="element-204">Parcel Sizes</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-205">CorkShip uses parcel size configurations to automatically assign the right carton to each order based on bottle count. You configure these once and CorkShip handles the rest.</p>
              <SubHeading data-id="element-206">Default configurations</SubHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-207">CorkShip ships with sensible default parcel sizes for standard wine cartons. You can customise these to match your actual carton dimensions and packaging weights in <strong data-id="element-208">Settings → Parcel Sizes</strong>.</p>
              <div className="overflow-x-auto mb-6" data-id="element-209">
                <table className="w-full text-sm border-collapse" data-id="element-210">
                  <thead data-id="element-211">
                    <tr className="bg-[#F8FAFC]" data-id="element-212">
                      <th className="text-left px-4 py-3 text-[#0F172A] font-semibold border-b border-[#E2E8F0]" data-id="element-213">Configuration</th>
                      <th className="text-left px-4 py-3 text-[#0F172A] font-semibold border-b border-[#E2E8F0]" data-id="element-214">Bottles</th>
                      <th className="text-left px-4 py-3 text-[#0F172A] font-semibold border-b border-[#E2E8F0]" data-id="element-215">Typical use</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-216">
                    {[{
                    config: '1-pack',
                    bottles: '1',
                    use: 'Single bottle gift orders'
                  }, {
                    config: '2-pack',
                    bottles: '2',
                    use: 'Small orders, gift pairs'
                  }, {
                    config: '3-pack',
                    bottles: '3',
                    use: 'Mixed three-packs'
                  }, {
                    config: '6-pack',
                    bottles: '6',
                    use: 'Half-case orders, wine clubs'
                  }, {
                    config: '12-pack',
                    bottles: '12',
                    use: 'Full case, wine club dispatches'
                  }].map((row, i) => <tr key={row.config} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'} data-id="element-217">
                        <td className="px-4 py-3 font-medium text-[#0F172A] border-b border-[#E2E8F0]" data-id="element-218">{row.config}</td>
                        <td className="px-4 py-3 text-[#475569] border-b border-[#E2E8F0]" data-id="element-219">{row.bottles}</td>
                        <td className="px-4 py-3 text-[#475569] border-b border-[#E2E8F0]" data-id="element-220">{row.use}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
              <Screenshot src={SCREENSHOTS.parcelSizes} alt="CorkShip Settings — Parcel Sizes" caption="Settings → Parcel Sizes — customise dimensions and packaging weights to match your cartons" data-id="element-221" />
              <Callout type="tip" data-id="element-222">Always enter your <strong data-id="element-223">packaging weight</strong> (the weight of the empty carton) separately from bottle weight. CorkShip adds these together to calculate the total parcel weight sent to Australia Post.</Callout>

              <SectionHeading id="creating-shipments" data-id="element-224">Creating Shipments</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-225">Shipments are created from the CorkShip Orders view, which shows all paid, unfulfilled Commerce7 orders ready to ship.</p>
              <SubHeading data-id="element-226">Single order</SubHeading>
              <Step n={1} title="Open CorkShip from your Commerce7 sidebar" data-id="element-227">Navigate to the <strong data-id="element-228">Orders</strong> tab. CorkShip loads your pending orders from Commerce7 and checks each address against Australia Post.</Step>
              <Step n={2} title="Review the order" data-id="element-229">Check the order details, bottle count, and destination address. Orders with address warnings are highlighted — fix these in Commerce7 before creating a shipment.</Step>
              <Step n={3} title="Click Create Shipment" data-id="element-230">CorkShip automatically assigns bottles to the optimal carton configuration and submits the shipment to Australia Post. You'll see the tracking number appear within seconds.</Step>
              <Screenshot src={SCREENSHOTS.shipments} alt="CorkShip Orders — shipments list" caption="The Orders tab showing pending orders with address validation indicators and shipment status" data-id="element-231" />
              <SubHeading data-id="element-232">Bulk orders</SubHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-233">To create shipments for multiple orders at once, use the <strong data-id="element-234">Bulk Create</strong> tab. Select the orders you want to process, review the summary, and click <strong data-id="element-235">Create All Shipments</strong>.</p>
              <Callout type="warning" data-id="element-236">Orders shipping to <strong data-id="element-237">Victoria (VIC)</strong> automatically include Identity on Delivery (IDENTITY_ON_DELIVERY) as required by Liquor Control Victoria legislation. The recipient will need to show ID at the door.</Callout>

              <SectionHeading id="labels" data-id="element-238">Printing Labels</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-239">Once a shipment has been created, you can generate the Australia Post shipping label as a PDF.</p>
              <Step n={1} title="Select the order in the Orders list" data-id="element-240">Orders with a shipment created show a <strong data-id="element-241">Print Label</strong> button.</Step>
              <Step n={2} title="Click Print Label" data-id="element-242">CorkShip fetches the label PDF from Australia Post and opens it in your browser. Print directly or save to print later.</Step>
              <Step n={3} title="Attach the label to your parcel" data-id="element-243">Print on A4 or thermal label stock. Attach securely to the outside of the carton with the barcode clearly visible and unobstructed.</Step>
              <Screenshot src={SCREENSHOTS.labels} alt="CorkShip — Generate Labels" caption="Generating a label for a single order — the PDF downloads instantly from Australia Post" data-id="element-244" />
              <Callout type="info" data-id="element-245">If an order has multiple parcels (e.g. two 6-packs), CorkShip generates a combined PDF with one label per parcel — all in a single download.</Callout>

              <SectionHeading id="manifests" data-id="element-246">Manifests</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-247">A manifest is a daily lodgement document that groups your shipments for Australia Post pickup or drop-off. You must close a manifest before handing parcels over.</p>
              <Step n={1} title="Open the Manifests tab in CorkShip" data-id="element-248">You'll see your current open manifest (created automatically when your first shipment of the day is lodged) and any past closed manifests.</Step>
              <Step n={2} title="Add shipments to the manifest" data-id="element-249">Shipments with labels printed are automatically added to the open manifest. You can review which shipments are included before closing.</Step>
              <Step n={3} title="Close the manifest" data-id="element-250">When ready to hand over to Australia Post, click <strong data-id="element-251">Close Manifest</strong>. This lodges the manifest with Australia Post and locks it — no further shipments can be added.</Step>
              <Step n={4} title="Download the Order Summary PDF" data-id="element-252">After closing, a <strong data-id="element-253">Download Summary</strong> link appears. This is the Australia Post manifest summary PDF — print it and hand it to the driver or keep it for your records.</Step>
              <Screenshot src={SCREENSHOTS.manifests} alt="CorkShip Manifests page" caption="The Manifests tab — open and closed manifests with the Download Summary option" data-id="element-254" />
              <Callout type="warning" data-id="element-255">Always close the manifest before your Australia Post driver arrives. Parcels included in an open (unclosed) manifest will not be scanned into the network correctly.</Callout>

              <SectionHeading id="address-validation" data-id="element-256">Address Validation</SectionHeading>
              <p className="text-[#475569] leading-relaxed mb-4" data-id="element-257">CorkShip automatically validates every order's suburb, state, and postcode against the Australia Post address database when you open the Orders page.</p>
              <div className="flex gap-4 mb-6 flex-wrap" data-id="element-258">
                <div className="flex items-center gap-2 text-sm" data-id="element-259">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D1FAE5] text-[#059669] font-bold text-xs" data-id="element-260">✓</span>
                  <span className="text-[#475569]" data-id="element-261">Address recognised by Australia Post</span>
                </div>
                <div className="flex items-center gap-2 text-sm" data-id="element-262">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#FEE2E2] text-[#DC2626] font-bold text-xs" data-id="element-263">✗</span>
                  <span className="text-[#475569]" data-id="element-264">Suburb / state / postcode mismatch — fix in Commerce7 before creating a shipment</span>
                </div>
              </div>
              <Callout type="info" data-id="element-265">Address validation checks the suburb, state, and postcode combination only — it does not verify the street address or unit number. Always double-check orders with unusual or rural addresses.</Callout>

              <SectionHeading id="postcode-rules" data-id="element-266">
                <span className="flex items-center gap-3" data-id="element-267">
                  Postcode Rules
                  <span className="inline-flex items-center gap-1.5 bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0] px-3 py-1 rounded-full text-xs font-semibold tracking-wide" data-id="element-268">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3" data-id="element-269"><circle cx="12" cy="12" r="10" data-id="element-270" /><path d="M12 6v6l4 2" data-id="element-271" /></svg>
                    Coming Soon
                  </span>
                </span>
              </SectionHeading>
              <div className="rounded-2xl border-2 border-dashed border-[#E2E8F0] bg-[#F8FAFC] p-10 text-center" data-id="element-272">
                <div className="w-14 h-14 rounded-2xl bg-[#F1F5F9] border border-[#E2E8F0] flex items-center justify-center mx-auto mb-5" data-id="element-273">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-7 h-7 text-[#94A3B8]" data-id="element-274"><circle cx="12" cy="10" r="3" data-id="element-275" /><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z" data-id="element-276" /></svg>
                </div>
                <p className="font-heading text-lg font-bold text-[#0F172A] mb-2" data-id="element-277">Postcode Rules — Coming Soon</p>
                <p className="text-[#64748B] text-sm leading-relaxed max-w-md mx-auto mb-6" data-id="element-278">Postcode rules will let you control which destinations CorkShip creates shipments for — useful for liquor licence restrictions or excluding uneconomical remote areas. This feature is currently in testing and will be available shortly.</p>
                <button disabled className="inline-flex items-center gap-2 bg-[#E2E8F0] text-[#94A3B8] px-6 py-2.5 rounded-lg text-sm font-semibold cursor-not-allowed select-none" data-id="element-279">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4" data-id="element-280"><circle cx="12" cy="12" r="10" data-id="element-281" /><path d="M12 6v6l4 2" data-id="element-282" /></svg>
                  Not yet available
                </button>
              </div>

              <SectionHeading id="troubleshooting" data-id="element-283">Troubleshooting</SectionHeading>
              {[{
              q: 'Shipment creation failed with an address error',
              a: "The suburb, state, and postcode on the order don't match the Australia Post database. Open the order in Commerce7 and correct the delivery address, then try again. Use the address validation indicator (✓/✗) in CorkShip to spot these before attempting to create a shipment."
            }, {
              q: 'My credentials are being rejected',
              a: "Double-check that you've entered the API Key, API Secret, and Account Number exactly as shown in MyPost Business — no extra spaces. Ensure you're in the correct mode (sandbox credentials only work in sandbox mode, live credentials only in live mode)."
            }, {
              q: "Labels aren't generating",
              a: 'Labels can only be generated after a shipment has been successfully created with Australia Post (tracking number visible). If the shipment shows an error status, delete it and create it again after fixing the underlying issue.'
            }, {
              q: "The manifest won't close",
              a: 'Ensure all shipments in the manifest have labels generated. Australia Post requires labels before a manifest can be lodged. Check for any shipments in a pending or error state and resolve them first.'
            }, {
              q: 'An order is missing from the CorkShip Orders view',
              a: "CorkShip only shows orders that are paid and unfulfilled with a shipping delivery method. Check in Commerce7 that the order's payment status is Paid and fulfilment status is Unfulfilled, and that the delivery method is set to Ship (not Pickup or Local Delivery)."
            }].map(item => <div key={item.q} className="mb-5 border border-[#E2E8F0] rounded-xl overflow-hidden" data-id="element-284">
                  <div className="flex items-start gap-3 p-4 bg-[#F8FAFC]" data-id="element-285">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-[#0284C7] flex-shrink-0 mt-0.5" data-id="element-286"><circle cx="12" cy="12" r="10" data-id="element-287" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" data-id="element-288" /><line x1="12" y1="17" x2="12.01" y2="17" data-id="element-289" /></svg>
                    <p className="font-semibold text-[#0F172A] text-sm" data-id="element-290">{item.q}</p>
                  </div>
                  <div className="p-4" data-id="element-291">
                    <p className="text-[#475569] text-sm leading-relaxed" data-id="element-292">{item.a}</p>
                  </div>
                </div>)}

              <div className="mt-10 p-6 bg-[#0F172A] rounded-2xl text-center" data-id="element-293">
                <p className="font-heading text-lg font-bold text-white mb-2" data-id="element-294">Still stuck?</p>
                <p className="text-white/60 text-sm mb-4" data-id="element-295">We're happy to help. Reach out and we'll get back to you within one business day.</p>
                <a href="mailto:support@corkship.io" className="inline-flex items-center gap-2 bg-[#0284C7] text-white px-6 py-2.5 rounded-lg text-sm font-semibold no-underline hover:bg-[#0369A1] transition-colors" data-id="element-296">Email Support</a>
              </div>

            </div>
          </main>
        </div>
      </div>

      <Footer data-id="element-297" />
    </div>;
}